/**
 * Basic statistical utilities for pattern validation
 */
export class StatisticalUtils {
  /**
   * Calculate chi-square test for pattern significance
   */
  static calculateChiSquare(
    observed: { wins: number; losses: number },
    expected: { winRate: number }
  ): { chiSquare: number; pValue: number; isSignificant: boolean } {
    const totalObserved = observed.wins + observed.losses;
    const expectedWins = totalObserved * (expected.winRate / 100);
    const expectedLosses = totalObserved * (1 - expected.winRate / 100);
    
    // Avoid division by zero
    if (expectedWins === 0 || expectedLosses === 0) {
      return { chiSquare: 0, pValue: 1, isSignificant: false };
    }
    
    const chiSquareWins = Math.pow(observed.wins - expectedWins, 2) / expectedWins;
    const chiSquareLosses = Math.pow(observed.losses - expectedLosses, 2) / expectedLosses;
    const chiSquare = chiSquareWins + chiSquareLosses;
    
    // Simple p-value approximation for 1 degree of freedom
    const pValue = this.chiSquareToPValue(chiSquare, 1);
    const isSignificant = pValue < 0.05; // 95% confidence level
    
    return { chiSquare, pValue, isSignificant };
  }
  
  /**
   * Simple chi-square to p-value conversion (approximation)
   */
  private static chiSquareToPValue(chiSquare: number, degreesOfFreedom: number): number {
    // Very basic approximation - for more accuracy, would need proper statistical library
    if (chiSquare < 0.004) return 0.95;
    if (chiSquare < 0.02) return 0.90;
    if (chiSquare < 0.10) return 0.75;
    if (chiSquare < 0.45) return 0.50;
    if (chiSquare < 1.64) return 0.20;
    if (chiSquare < 2.71) return 0.10;
    if (chiSquare < 3.84) return 0.05; // Critical value for 95% confidence
    if (chiSquare < 6.63) return 0.01;
    return 0.001;
  }
  
  /**
   * Calculate confidence interval for success rate
   */
  static calculateConfidenceInterval(
    successes: number,
    total: number,
    confidenceLevel: number = 0.95
  ): { lower: number; upper: number } {
    if (total === 0) return { lower: 0, upper: 100 };
    
    const p = successes / total;
    const z = confidenceLevel === 0.95 ? 1.96 : 2.58; // 95% or 99%
    const margin = z * Math.sqrt((p * (1 - p)) / total);
    
    return {
      lower: Math.max(0, (p - margin) * 100),
      upper: Math.min(100, (p + margin) * 100)
    };
  }
  
  /**
   * Check if sample size is adequate for statistical significance
   */
  static isAdequateSampleSize(
    successes: number,
    total: number,
    minExpectedCount: number = 5
  ): { isAdequate: boolean; reason: string } {
    if (total < 10) {
      return { isAdequate: false, reason: 'Total sample size too small (< 10)' };
    }
    
    if (successes < minExpectedCount || (total - successes) < minExpectedCount) {
      return { 
        isAdequate: false, 
        reason: `Need at least ${minExpectedCount} wins and ${minExpectedCount} losses` 
      };
    }
    
    return { isAdequate: true, reason: 'Sample size adequate for statistical testing' };
  }
  
  /**
   * Detect potential bias in patterns
   */
  static detectBias(patterns: Array<{ successRate: number; frequency: number }>): {
    hasBias: boolean;
    biasType: 'win_bias' | 'loss_bias' | 'none';
    severity: 'low' | 'medium' | 'high';
    description: string;
  } {
    if (patterns.length === 0) {
      return { hasBias: false, biasType: 'none', severity: 'low', description: 'No patterns to analyze' };
    }
    
    const weightedSuccessRate = patterns.reduce((sum, p) => 
      sum + (p.successRate * p.frequency), 0
    ) / patterns.reduce((sum, p) => sum + p.frequency, 0);
    
    let biasType: 'win_bias' | 'loss_bias' | 'none' = 'none';
    let severity: 'low' | 'medium' | 'high' = 'low';
    
    if (weightedSuccessRate > 70) {
      biasType = 'win_bias';
      severity = weightedSuccessRate > 80 ? 'high' : 'medium';
    } else if (weightedSuccessRate < 30) {
      biasType = 'loss_bias';  
      severity = weightedSuccessRate < 20 ? 'high' : 'medium';
    }
    
    const description = biasType === 'none' 
      ? 'No significant bias detected'
      : `${severity.toUpperCase()} ${biasType.replace('_', ' ')} detected (${weightedSuccessRate.toFixed(1)}% win rate)`;
    
    return {
      hasBias: biasType !== 'none',
      biasType,
      severity,
      description
    };
  }
}